import React from 'react';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import Badge from '../../components/ui/Badge/Badge';
import cvData from '../../assets/data/cv-data';
import './Profile.css';

const Profile = () => {
  const { personalInfo, skills, projects, experience, certifications } = cvData;

  return (
    <div className="profile-page bg-light">
      <div className="profile-cover bg-primary position-relative mb-5">
        <Container>
          <Row>
            <Col className="text-center">
              <div className="profile-avatar-wrapper">
                <img
                  src={personalInfo.photo}
                  alt={personalInfo.name}
                  className="profile-avatar rounded-circle border border-4 border-white shadow"
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        <Row className="mb-4">
          <Col className="text-center">
            <h1 className="display-5 fw-bold mb-2">{personalInfo.name}</h1>
            <p className="h5 text-primary mb-3">{personalInfo.title}</p>
            <p className="text-muted mb-4">{personalInfo.location}</p>

            <div className="social-links d-flex justify-content-center gap-3">
              {personalInfo.socialLinks.github && (
                <a href={personalInfo.socialLinks.github} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary rounded-circle">
                  <i className="fab fa-github"></i>
                </a>
              )}
              {personalInfo.socialLinks.linkedin && (
                <a href={personalInfo.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary rounded-circle">
                  <i className="fab fa-linkedin-in"></i>
                </a>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mb-5">
          <Col lg={8} className="mb-4 mb-lg-0">
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h3 className="h5 fw-bold mb-3">About Me</h3>
                <p className="text-muted mb-4">{personalInfo.bio}</p>

                <h4 className="h6 fw-bold mb-3">Quick Facts</h4>
                <Row className="g-3">
                  <Col sm={6}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-envelope text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Email</small>
                        <span>{personalInfo.email}</span>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-phone text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Phone</small>
                        <span>{personalInfo.phone}</span>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-calendar text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Experience</small>
                        <span>3+ years across teaching and software delivery</span>
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-project-diagram text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Projects</small>
                        <span>{projects.length} featured academic and software projects</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h3 className="h6 fw-bold mb-3">Skills Overview</h3>
                {Object.entries(skills).map(([category, skillList]) => (
                  <div key={category} className="mb-3">
                    <p className="small text-muted mb-2">{category.charAt(0).toUpperCase() + category.slice(1)}</p>
                    <div className="d-flex flex-wrap gap-2">
                      {skillList.slice(0, 5).map((skill) => (
                        <Badge key={skill.name} variant="primary" className="px-3 py-2">
                          {skill.name}
                        </Badge>
                      ))}
                      {skillList.length > 5 && (
                        <Badge variant="light" className="px-3 py-2">
                          +{skillList.length - 5}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <section className="mb-5">
          <h2 className="h3 fw-bold text-center mb-5">
            <span className="border-bottom border-primary border-3 pb-2">Technical Skills</span>
          </h2>

          <Row className="g-4">
            {Object.entries(skills).map(([category, skillList]) => (
              <Col md={4} key={category}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <h3 className="h5 fw-bold mb-4 text-capitalize">
                      <i className={`fas fa-${category === 'frontend' ? 'code' : category === 'backend' ? 'server' : 'tools'} text-primary me-2`}></i>
                      {category}
                    </h3>
                    {skillList.map((skill) => (
                      <div key={skill.name} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small">{skill.name}</span>
                          <span className="small text-primary">{skill.level}%</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-primary"
                            style={{ width: `${skill.level}%` }}
                            role="progressbar"
                          ></div>
                        </div>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="h3 fw-bold text-center mb-5">
            <span className="border-bottom border-primary border-3 pb-2">Selected Projects</span>
          </h2>

          <Row className="g-4">
            {projects.slice(0, 3).map((project) => (
              <Col md={4} key={project.id}>
                <Card className="border-0 shadow-sm h-100 project-card">
                  <div className="position-relative overflow-hidden" style={{ height: '200px' }}>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-100 h-100 object-fit-cover"
                    />
                    <div className="position-absolute top-0 end-0 m-3">
                      <Badge variant={project.featured ? 'warning' : 'secondary'}>
                        {project.featured ? 'Featured' : 'Project'}
                      </Badge>
                    </div>
                  </div>
                  <CardBody>
                    <h3 className="h6 fw-bold mb-2">{project.title}</h3>
                    <p className="small text-muted mb-3">{project.description}</p>
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {project.technologies.slice(0, 3).map((tech) => (
                        <Badge key={tech} variant="light" className="small">{tech}</Badge>
                      ))}
                    </div>
                    <div className="d-flex gap-2">
                      <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary flex-grow-1">
                        <i className="fas fa-external-link-alt me-1"></i> View
                      </a>
                      <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                        <i className="fab fa-github"></i>
                      </a>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        <section className="mb-5">
          <h2 className="h3 fw-bold text-center mb-5">
            <span className="border-bottom border-primary border-3 pb-2">Work Experience</span>
          </h2>

          <Row>
            <Col lg={8} className="mx-auto">
              <div className="timeline">
                {experience.map((exp) => (
                  <div key={exp.id} className="timeline-item">
                    <Card className="border-0 shadow-sm mb-4">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h3 className="h5 fw-bold mb-1">{exp.position}</h3>
                            <p className="text-primary mb-0">{exp.company}</p>
                          </div>
                          <Badge variant="light" className="px-3 py-2">
                            {exp.startDate} - {exp.endDate}
                          </Badge>
                        </div>
                        <p className="small text-muted mb-3">{exp.description}</p>
                        <ul className="small list-unstyled">
                          {exp.achievements.map((achievement, idx) => (
                            <li key={idx} className="mb-2">
                              <i className="fas fa-check-circle text-success me-2"></i>
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </CardBody>
                    </Card>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </section>

        <section>
          <h2 className="h3 fw-bold text-center mb-5">
            <span className="border-bottom border-primary border-3 pb-2">Training Highlights</span>
          </h2>

          <Row className="g-4">
            {certifications.map((cert) => (
              <Col md={4} key={cert.id}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-certificate text-warning fa-2x me-3"></i>
                      <div>
                        <h3 className="h6 fw-bold mb-1">{cert.name}</h3>
                        <p className="small text-muted mb-2">{cert.issuer}</p>
                        <Badge variant="light" className="small">{cert.date}</Badge>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </section>
      </Container>
    </div>
  );
};

export default Profile;
